import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Alert, AppBar, Box, Button, CircularProgress, Container, Drawer, IconButton, Paper, Stack, Toolbar, Typography, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightFooter from '../components/CopyrightFooter';
import ProfileMenu from './ProfileMenu';
import { useAuth } from './AuthContext';
import LoginScreen from './LoginScreen';
import appBackground from '../assets/images/wallpaper_padel_3.jpg';
import mobileBackground from '../assets/images/wallpaper_padel_1.jpg';
import { APP_VERSION } from '../constants';

function Home() {
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  async function signOut() {
    setBusy(true);
    setError('');
    try { await logout(); } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  }
  return <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: '#123747',
    backgroundImage: `linear-gradient(rgba(5, 24, 34, 0.2), rgba(5, 24, 34, 0.35)), url(${isMobile ? mobileBackground : appBackground})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  }}>
    <AppBar position="sticky" sx={{ bgcolor: '#123747' }}>
      <Toolbar sx={{ gap: { xs: 1, sm: 2 }, minHeight: { xs: 48, sm: 52 }, px: { xs: 1.5, sm: 2 } }}>
        <IconButton color="inherit" edge="start" aria-label="Abrir menú" aria-expanded={menuOpen}
          aria-controls={menuOpen ? 'main-menu' : undefined} onClick={() => setMenuOpen(true)}>
          <MenuIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <Typography noWrap title={user.nombre || user.username} sx={{ minWidth: 0, fontWeight: 500 }}>
          {user.nombre || user.username}
        </Typography>
        <ProfileMenu user={user} />
        <Button color="inherit" disabled={busy} onClick={signOut} sx={{ flexShrink: 0, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.6)' } }}>
          {busy ? 'Cerrando…' : 'Cerrar sesión'}
        </Button>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.72)', whiteSpace: 'nowrap' }}>
          {APP_VERSION}
        </Typography>
      </Toolbar>
    </AppBar>
    <Drawer anchor="left" open={menuOpen} onClose={() => setMenuOpen(false)}
      PaperProps={{ sx: { width: 280, maxWidth: '85vw' } }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">Menú</Typography>
        <IconButton aria-label="Cerrar menú" onClick={() => setMenuOpen(false)}><CloseIcon /></IconButton>
      </Toolbar>
      <Box component="nav" id="main-menu" aria-label="Menú principal" sx={{ flexGrow: 1 }} />
    </Drawer>
    <Container component="main" maxWidth="md" sx={{ py: { xs: 4, sm: 8 }, flexGrow: 1 }}>
    <Paper sx={{ p: { xs: 3, sm: 5 }, bgcolor: 'rgba(255, 255, 255, 0.96)', borderRadius: 3 }} variant="outlined"><Stack spacing={3}>
      <Typography variant="h4" component="h1">Hola, {user.nombre}</Typography>
      <Typography color="text.secondary">Este es el inicio de tu nuevo proyecto.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack></Paper>
    </Container>
    <CopyrightFooter />
  </Box>;
}
export default function AuthRoutes() {
  const { user, loading, error, restore } = useAuth();
  if (loading) return <Box role="status" aria-label="Verificando sesión" sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="error">{error}</Alert><Button onClick={restore}>Reintentar</Button></Container>;
  return <Routes>
    <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginScreen />} />
    <Route path="/" element={user ? <Home /> : <Navigate to="/login" replace />} />
    <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
  </Routes>;
}
